'use client';

// Tela Inicial (Dashboard "Início") — épico ini, fiel a design-import/src/pages/inicio.jsx.
// Consome GET /api/ia/dashboard/* (mod-ia). Cada bloco carrega e degrada isolado.

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { Chip, DocCircle, AvatarStack, type ChipVariant } from '@/components/ui';
import ComparacaoPrecedenteModal from '@/components/dashboard/ComparacaoPrecedenteModal';
import {
  buscarMetricasDashboard,
  buscarProcessosAtivos,
  buscarTarefasPendentes,
  buscarAtividadesRecentes,
  buscarCopilotSugestoes,
  toDocStatus,
  fmtMoney,
  type Metricas,
  type ProcessoAtivo,
  type TarefaPendente,
  type AtividadeItem,
  type CopilotSugestao,
  type CopilotAcao,
} from '@/lib/dashboard-api';

const ESTADO_CHIP: Record<string, ChipVariant> = {
  instrucao: 'info',
  elaboracao: 'warning',
  revisao: 'brand',
  concluido: 'success',
  cancelado: 'neutral',
};
// Cor do fill da barra de progresso por estado (espelha o protótipo).
const PROGRESS_MOD: Record<string, string> = {
  elaboracao: 'warning',
  instrucao: 'info',
  // revisao usa o brand (default)
};

function saudacaoHora(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

export default function Dashboard() {
  const router = useRouter();
  const { usuario } = useAuth();

  const [metricas, setMetricas] = useState<Metricas | null>(null);
  const [processos, setProcessos] = useState<ProcessoAtivo[] | null>(null);
  const [tarefas, setTarefas] = useState<{ total: number; tarefas: TarefaPendente[] } | null>(null);
  const [atividades, setAtividades] = useState<AtividadeItem[] | null>(null);
  const [sugestoes, setSugestoes] = useState<CopilotSugestao[] | null>(null);
  // Modal de comparação com precedente (ui-1.4-2).
  const [comparar, setComparar] = useState<{ atual: string; precedente: string; precId: string | null } | null>(null);

  useEffect(() => {
    buscarMetricasDashboard().then(setMetricas).catch(() => setMetricas(null));
    buscarProcessosAtivos(6).then((r) => setProcessos(r.processos)).catch(() => setProcessos([]));
    buscarTarefasPendentes(3).then(setTarefas).catch(() => setTarefas({ total: 0, tarefas: [] }));
    buscarAtividadesRecentes(8).then((r) => setAtividades(r.atividades)).catch(() => setAtividades([]));
    buscarCopilotSugestoes().then((r) => setSugestoes(r.sugestoes)).catch(() => setSugestoes([]));
  }, []);

  const primeiroNome = (usuario?.nome ?? '').split(' ')[0] || 'usuário';
  const numTarefas = tarefas?.total ?? metricas?.tarefas_pendentes.valor ?? 0;
  const numProcessos = metricas?.processos_ativos.valor ?? processos?.length ?? 0;

  const abrirAcao = (a: CopilotAcao) => {
    if (!a.processo_id) return;
    const seg = a.rota === 'elaboracao' ? 'elaboracao' : 'painel';
    router.push(`/processos/${a.processo_id}/${seg}`);
  };

  // Agrupa atividades por dia preservando ordem.
  const gruposAtividade: { dia: string; itens: AtividadeItem[] }[] = [];
  for (const a of atividades ?? []) {
    const ult = gruposAtividade[gruposAtividade.length - 1];
    if (ult && ult.dia === a.grupo_dia) ult.itens.push(a);
    else gruposAtividade.push({ dia: a.grupo_dia, itens: [a] });
  }

  const mv = (n: number | null | undefined) => (n == null ? '—' : String(n));

  return (
    <div>
      {/* Saudação */}
      <div className="saudacao">
        <span className="hi">
          {saudacaoHora()}, {primeiroNome}
        </span>
        <span className="sub">
          você tem{' '}
          <b style={{ color: 'var(--warning-600)' }}>
            {numTarefas} tarefa{numTarefas === 1 ? '' : 's'} pendente{numTarefas === 1 ? '' : 's'}
          </b>{' '}
          e <b>{numProcessos} processo{numProcessos === 1 ? '' : 's'} ativo{numProcessos === 1 ? '' : 's'}</b> hoje
        </span>
      </div>

      {/* Stat cards */}
      <div className="stat-row">
        <div className="stat-card brand">
          <div className="label"><i className="pi pi-folder" />Processos ativos</div>
          <div className="value">{mv(metricas?.processos_ativos.valor)}</div>
          <div className={`trend ${(metricas?.processos_ativos.trend_semana ?? 0) > 0 ? 'up' : ''}`}>
            {(metricas?.processos_ativos.trend_semana ?? 0) > 0
              ? `↑ ${metricas?.processos_ativos.trend_semana} esta semana`
              : 'sem novos esta semana'}
          </div>
        </div>
        <div className="stat-card warning">
          <div className="label"><i className="pi pi-exclamation-triangle" />Tarefas pendentes</div>
          <div className="value">{mv(metricas?.tarefas_pendentes.valor)}</div>
          <div className="trend">{metricas?.tarefas_pendentes.atribuidas_voce ?? 0} atribuídas a você</div>
        </div>
        <div className="stat-card">
          <div className="label"><i className="pi pi-file" />Documentos gerados</div>
          <div className="value">{mv(metricas?.documentos_gerados.valor)}</div>
          <div className="trend">{metricas?.documentos_gerados.ultimos_30_dias ?? 0} nos últimos 30 dias</div>
        </div>
        <div className="stat-card success">
          <div className="label"><i className="pi pi-check-circle" />Taxa de aprovação IA</div>
          <div className="value">
            {metricas?.taxa_aprovacao_ia.valor_pct != null ? `${metricas.taxa_aprovacao_ia.valor_pct}%` : '—'}
          </div>
          <div className={`trend ${(metricas?.taxa_aprovacao_ia.delta_mes_pct ?? 0) >= 0 ? 'up' : 'down'}`}>
            {metricas?.taxa_aprovacao_ia.delta_mes_pct != null
              ? `${metricas.taxa_aprovacao_ia.delta_mes_pct >= 0 ? '↑' : '↓'} ${Math.abs(metricas.taxa_aprovacao_ia.delta_mes_pct)}% vs mês anterior`
              : 'sem histórico ainda'}
          </div>
        </div>
      </div>

      <div className="inicio-grid">
        {/* Coluna esquerda */}
        <div className="min-w-0">
          {/* Tarefas pendentes */}
          <div className="dash-card" style={{ marginBottom: 20 }}>
            <div className="dash-card-header">
              <h3><i className="pi pi-inbox" style={{ marginRight: 6, color: 'var(--warning-600)' }} />Minhas tarefas pendentes</h3>
              <button className="text-xs font-medium text-[var(--brand-600)] hover:underline" onClick={() => router.push('/caixa')}>
                Ver todas <i className="pi pi-arrow-right" style={{ fontSize: 10 }} />
              </button>
            </div>
            <div className="dash-card-body flush">
              {tarefas === null ? (
                <div className="px-[18px] py-4 text-sm text-[var(--fg-muted)]">Carregando…</div>
              ) : tarefas.tarefas.length === 0 ? (
                <div className="px-[18px] py-6 text-center text-sm text-[var(--fg-muted)]">Nenhuma tarefa pendente. 🎉</div>
              ) : (
                <div className="task-list">
                  {tarefas.tarefas.map((t) => (
                    <button
                      key={t.id}
                      className="task-item"
                      onClick={() => (t.processo_id ? router.push(`/processos/${t.processo_id}/painel`) : router.push('/caixa'))}
                    >
                      <div className={`task-icon ${t.tipo}`}><i className={`pi ${t.icone}`} /></div>
                      <div className="task-body">
                        <div className="task-title">{t.titulo}</div>
                        <div className="task-meta">
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{t.processo}</span>
                          <span className="dot-sep" />
                          <span>{t.origem}</span>
                          <span className="dot-sep" />
                          <span>{t.quando}</span>
                        </div>
                      </div>
                      <i className="pi pi-chevron-right self-center text-[var(--fg-subtle)] text-xs" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Processos ativos */}
          <div className="dash-card">
            <div className="dash-card-header">
              <h3><i className="pi pi-folder-open" style={{ marginRight: 6, color: 'var(--brand-600)' }} />Meus processos ativos</h3>
              <button
                className="inline-flex items-center gap-1 rounded-md bg-[var(--brand-600)] px-2.5 py-1 text-xs font-medium text-white hover:opacity-90"
                onClick={() => router.push('/processos/novo')}
              >
                <i className="pi pi-plus" style={{ fontSize: 10 }} />Novo processo
              </button>
            </div>
            <div className="dash-card-body flush">
              {processos === null ? (
                <div className="px-4 py-4 text-sm text-[var(--fg-muted)]">Carregando…</div>
              ) : processos.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-[var(--fg-muted)]">Nenhum processo ativo. Crie um novo para começar.</div>
              ) : (
                processos.map((p) => {
                  const aprovados = p.docs.filter((d) => d.status === 'aprovado').length;
                  return (
                    <button key={p.id} className="process-card" onClick={() => router.push(`/processos/${p.id}/painel`)}>
                      <div className="line1">
                        <span className="code">{p.codigo ?? '—'}</span>
                        <span className="text-[var(--fg-subtle)]">·</span>
                        <span className="obj truncate">{p.objeto}</span>
                      </div>
                      <div className="line2">
                        {p.modalidade && <Chip variant="neutral">{p.modalidade}</Chip>}
                        {p.valor_estimado != null && (
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtMoney(p.valor_estimado)}</span>
                        )}
                        <span className={`dash-progress ${PROGRESS_MOD[p.estado] ?? ''}`} style={{ width: 120 }}>
                          <span className="fill" style={{ width: `${p.progresso_pct}%`, display: 'block' }} />
                        </span>
                        <Chip variant={ESTADO_CHIP[p.estado] ?? 'neutral'} dot>{p.estado_label}</Chip>
                      </div>
                      <div className="line3">
                        {p.docs.length > 0 && (
                          <>
                            <span className="doc-status-row">
                              {p.docs.map((d) => (
                                <DocCircle key={d.key} status={toDocStatus(d.status)} title={`${d.key.toUpperCase()}: ${d.status}`} />
                              ))}
                            </span>
                            <span className="text-xs text-[var(--fg-muted)]">{aprovados} de {p.docs.length} docs</span>
                          </>
                        )}
                        {p.participantes.length > 0 && (
                          <>
                            <span className="text-[var(--fg-subtle)]">·</span>
                            <AvatarStack pessoas={p.participantes.map((pt) => ({ nome: pt.nome }))} max={4} />
                            <span className="text-xs text-[var(--fg-muted)]">
                              {p.participantes.length} participante{p.participantes.length > 1 ? 's' : ''}
                            </span>
                          </>
                        )}
                        {p.alertas > 0 && (
                          <>
                            <span className="text-[var(--fg-subtle)]">·</span>
                            <Chip variant="warning">
                              <i className="pi pi-exclamation-triangle" style={{ fontSize: 10 }} />{p.alertas} alerta{p.alertas > 1 ? 's' : ''}
                            </Chip>
                          </>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Coluna direita */}
        <div className="min-w-0">
          {/* Copilot sugere */}
          <div className="dash-card" style={{ marginBottom: 20 }}>
            <div className="dash-card-header">
              <h3><i className="pi pi-bolt" style={{ marginRight: 6, color: 'var(--ai-500)' }} />Copilot sugere</h3>
            </div>
            <div className="dash-card-body" style={{ padding: 14 }}>
              {sugestoes === null ? (
                <div className="text-sm text-[var(--fg-muted)]">Carregando…</div>
              ) : sugestoes.length === 0 ? (
                <div className="text-sm text-[var(--fg-muted)]">Sem sugestões no momento.</div>
              ) : (
                sugestoes.map((s, i) => (
                  <div
                    key={i}
                    className={`guardrail-alert ${s.tipo === 'precedente' ? 'ai' : ''}`}
                    style={{ margin: 0, marginBottom: i < sugestoes.length - 1 ? 10 : 0 }}
                  >
                    <i className={`pi ${s.tipo === 'precedente' ? 'pi-sparkles' : 'pi-clock'}`} />
                    <div className="ga-body">
                      <div className="ga-title">{s.titulo}</div>
                      <div>{s.texto}</div>
                      <div className="ga-actions">
                        {s.tipo === 'precedente' ? (
                          <button
                            className="inline-flex items-center gap-1 rounded-md bg-[var(--ai-500)] px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                            disabled={!s.processo_atual || !(s.precedente_codigo || s.precedente_id)}
                            onClick={() =>
                              setComparar({
                                atual: s.processo_atual!,
                                precedente: (s.precedente_codigo || s.precedente_id)!,
                                precId: s.precedente_id ?? null,
                              })
                            }
                          >
                            <i className="pi pi-sparkles" style={{ fontSize: 10 }} />
                            Ver comparação
                          </button>
                        ) : (
                          <button
                            className="inline-flex items-center gap-1 rounded-md bg-[var(--brand-600)] px-2.5 py-1 text-xs font-medium text-white hover:opacity-90"
                            onClick={() => abrirAcao(s.acao)}
                          >
                            <i className="pi pi-arrow-right" style={{ fontSize: 10 }} />
                            Abrir
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Atividade recente */}
          <div className="dash-card">
            <div className="dash-card-header">
              <h3><i className="pi pi-clock" style={{ marginRight: 6 }} />Atividade recente</h3>
            </div>
            <div className="dash-card-body flush">
              {atividades === null ? (
                <div className="px-[18px] py-4 text-sm text-[var(--fg-muted)]">Carregando…</div>
              ) : atividades.length === 0 ? (
                <div className="px-[18px] py-6 text-center text-sm text-[var(--fg-muted)]">Nenhuma atividade recente.</div>
              ) : (
                <div className="timeline">
                  {gruposAtividade.map((g, gi) => (
                    <div key={`${g.dia}-${gi}`}>
                      <div className="timeline-day">{g.dia}</div>
                      {g.itens.map((a) => (
                        <div key={a.id} className={`timeline-item ${a.tipo}`}>
                          <span className="time">{a.hora}</span>
                          <div className="what">
                            {a.autor && <b>{a.autor}</b>} {a.texto}
                            {a.processo_codigo ? ` · ${a.processo_codigo}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {comparar && (
        <ComparacaoPrecedenteModal
          atual={comparar.atual}
          precedente={comparar.precedente}
          onAbrirPrecedente={
            comparar.precId
              ? () => {
                  router.push(`/processos/${comparar.precId}/painel`);
                  setComparar(null);
                }
              : undefined
          }
          onClose={() => setComparar(null)}
        />
      )}
    </div>
  );
}
